<template>
  <section class="product-faq-editor">
    <div class="faq-label">
      <span>{{ titleText }}</span>
    </div>
    <div class="faq-content">
      <div v-if="isFaqProcessing" class="faq-loading">
        <i class="el-icon-loading"></i>
        <span>{{ loadingText }}</span>
      </div>
      <template v-if="!isFaqProcessing || hasFaqContent">
        <div v-for="(item, index) in faqItems" :key="index" class="faq-card">
          <div class="faq-card-head">
            <strong>{{ qaPrefix }}-{{ index + 1 }}</strong>
            <div class="faq-card-actions">
              <button
                v-if="!editorDisabled"
                type="button"
                class="faq-polish"
                :disabled="polishLoading"
                @click="polishItem(index)"
              >
                <i v-if="isPolishing(index)" class="el-icon-loading"></i>
                {{ isPolishing(index) ? polishingText : polishText }}
              </button>
              <button v-if="!editorDisabled" type="button" class="faq-delete" @click="removeItem(index)">{{ deleteText }}</button>
            </div>
          </div>
          <el-input
            :value="item.question"
            :disabled="editorDisabled"
            :placeholder="questionPlaceholder"
            @input="updateItem(index, 'question', $event)"
          />
          <el-input
            type="textarea"
            :rows="4"
            :value="item.answer"
            :disabled="editorDisabled"
            :placeholder="answerPlaceholder"
            @input="updateItem(index, 'answer', $event)"
          />
        </div>
      </template>
      <el-button v-if="!editorDisabled" size="small" class="faq-add" @click="addItem">{{ addText }}</el-button>
    </div>
  </section>
</template>

<script>
import { SITE_TYPE } from '@/utils/product-case/constants';

function createEmptyFaq() {
  return { question: '', answer: '' };
}

function normalizeEditorFaq(item) {
  const normalized = {
    question: String((item && item.question) || '').trim(),
    answer: String((item && item.answer) || '').trim()
  };
  if (item && item.focus_angle) {
    normalized.focus_angle = String(item.focus_angle).trim();
  }
  return normalized;
}

export default {
  name: 'ProductFaqEditor',
  props: {
    part: {
      type: Object,
      required: true
    },
    siteType: {
      type: Number,
      default: SITE_TYPE.CN
    },
    disabled: {
      type: Boolean,
      default: false
    },
    polishLoading: {
      type: Boolean,
      default: false
    },
    polishField: {
      type: String,
      default: ''
    }
  },
  computed: {
    faqItems() {
      const list = Array.isArray(this.part.faqList) ? this.part.faqList : [];
      return list.length ? list : [createEmptyFaq()];
    },
    isFaqProcessing() {
      return this.part && this.part.faqStatus === 'processing';
    },
    editorDisabled() {
      return this.disabled || this.isFaqProcessing;
    },
    hasFaqContent() {
      return this.faqItems.some(item => {
        const normalized = normalizeEditorFaq(item);
        return normalized.question || normalized.answer;
      });
    },
    loadingText() {
      return 'AI正在生成常见咨询问题...';
    },
    deleteText() {
      return '删除';
    },
    addText() {
      return '添加';
    },
    titleText() {
      return '常见咨询问题';
    },
    qaPrefix() {
      return '问&答';
    },
    questionPlaceholder() {
      return '问题';
    },
    answerPlaceholder() {
      return '答案';
    },
    polishText() {
      return 'AI润色';
    },
    polishingText() {
      return 'AI润色中';
    }
  },
  methods: {
    isPolishing(index) {
      return this.polishLoading && this.polishField === `faq:${index}`;
    },
    polishItem(index) {
      this.$emit('polish', {
        refineField: 'faq',
        faqItemIndex: index
      });
    },
    syncList(list) {
      this.$set(this.part, 'faqList', list);
    },
    updateItem(index, field, value) {
      const list = this.faqItems.map(normalizeEditorFaq);
      list[index][field] = value;
      this.syncList(list);
    },
    addItem() {
      this.syncList([...this.faqItems.map(normalizeEditorFaq), createEmptyFaq()]);
    },
    removeItem(index) {
      const list = this.faqItems.filter((item, itemIndex) => itemIndex !== index).map(normalizeEditorFaq);
      this.syncList(list);
    }
  }
};
</script>

<style lang="scss" scoped>
.product-faq-editor {
  display: grid;
  grid-template-columns: 180px minmax(0, 1fr);
  gap: 18px;
  align-items: flex-start;
  grid-column: 1 / -1;
}
.faq-label {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding-top: 12px;
  color: #061c3c;
  font-size: 14px;
  font-weight: 700;
}
.faq-polish {
  border: 1px solid transparent;
  border-radius: 4px;
  padding: 3px 8px;
  background: transparent;
  color: #1a5cff;
  font-size: 13px;
  font-weight: 800;
}
.faq-polish:not(:disabled) {
  cursor: pointer;
}
.faq-polish:disabled {
  cursor: not-allowed;
}
.faq-polish:hover,
.faq-polish:focus {
  border-color: #cfe0ff;
  background: #f3f7ff;
}
.faq-polish i {
  margin-right: 4px;
}
.faq-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.faq-loading {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 16px;
  border: 1px solid #cfe0ff;
  border-radius: 6px;
  background: #f3f7ff;
  color: #1a5cff;
  font-size: 13px;
  font-weight: 700;
}
.faq-card {
  padding: 13px 14px 20px;
  border: 1px solid #dbe5f1;
  border-radius: 6px;
  background: #fff;
}
.faq-card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}
.faq-card-head strong {
  color: #071f43;
  font-size: 14px;
}
.faq-card-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}
.faq-delete {
  border: 0;
  background: transparent;
  color: #f04b55;
  cursor: pointer;
}
.faq-card ::v-deep .el-input {
  margin-bottom: 14px;
}
.faq-card ::v-deep .el-input__inner,
.faq-card ::v-deep .el-textarea__inner {
  border-color: #d7e1ee;
}
.faq-add {
  width: 56px;
}
.faq-add:not(:disabled) {
  cursor: pointer;
}
</style>
